/**
 * Shared display-formatting helpers for the backoffice components.
 */

/** Formats a euro amount with Spanish locale and 2 decimals ("1.234,56 €"). */
export function formatEuros(value: number | null | undefined): string {
	if (value === null || value === undefined) return '—';
	return `${value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** Formats a date as dd/mm/yyyy with the Spanish locale ("18/08/2026"); falsy input returns '—'. */
export function formatDate(value: string | Date | null | undefined): string {
	if (!value) return '—';
	return new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Formats a number with Spanish separators ("12.500"); null/undefined return '—'. */
export function formatNumber(value: number | null | undefined, options?: Intl.NumberFormatOptions): string {
	if (value === null || value === undefined) return '—';
	return value.toLocaleString('es-ES', options);
}

/** Converts a Date (or date-like string) to 'YYYY-MM-DD'; falsy input returns null. */
export function toIsoDate(value: unknown): string | null {
	if (!value) return null;
	if (value instanceof Date) {
		if (Number.isNaN(value.getTime())) return null;
		const year = value.getFullYear();
		const month = `${value.getMonth() + 1}`.padStart(2, '0');
		const day = `${value.getDate()}`.padStart(2, '0');
		return `${year}-${month}-${day}`;
	}
	return String(value);
}

/**
 * Converts a Date to 'YYYY-MM' using the local year and month.
 * Do not use `toISOString().slice(0, 7)`: in Europe/Madrid a local midnight
 * on the 1st can already belong to the previous month in UTC.
 */
export function toYearMonth(value: Date | null | undefined): string | null {
	if (!value || Number.isNaN(value.getTime())) return null;
	const year = value.getFullYear();
	const month = `${value.getMonth() + 1}`.padStart(2, '0');
	return `${year}-${month}`;
}

/** Local Date on the 1st of a `YYYY-MM` period. Invalid input returns null. */
export function dateFromYearMonth(period: string | null | undefined): Date | null {
	if (!period) return null;
	const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
	if (!match) return null;
	return new Date(Number(match[1]), Number(match[2]) - 1, 1);
}
